import React, { useState, useEffect } from 'react'
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import DashboardIcon from '@mui/icons-material/Dashboard'
import ApartmentIcon from '@mui/icons-material/Apartment'
import ContactsIcon from '@mui/icons-material/Contacts'
import HomeWorkIcon from '@mui/icons-material/HomeWork'
import ViewListIcon from '@mui/icons-material/ViewList'
import DescriptionIcon from '@mui/icons-material/Description'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import ReceiptIcon from '@mui/icons-material/Receipt'
import BalanceIcon from '@mui/icons-material/Balance'
import TuneIcon from '@mui/icons-material/Tune'
import GroupsIcon from '@mui/icons-material/Groups'
import GavelIcon from '@mui/icons-material/Gavel'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import EditNoteIcon from '@mui/icons-material/EditNote'
import PersonIcon from '@mui/icons-material/Person'
import LogoutIcon from '@mui/icons-material/Logout'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import PeopleIcon from '@mui/icons-material/People'
import MailIcon from '@mui/icons-material/Mail'
import ShieldIcon from '@mui/icons-material/Shield'
import { Link, router, usePage } from '@inertiajs/react'
import TimeLapseControl from '../ui/TimeLapseControl'

const MAIN_DRAWER_WIDTH = 220
const MAIN_DRAWER_COLLAPSED = 64
const PROPERTY_DRAWER_WIDTH = 220
const APPBAR_HEIGHT = 48

function NavSection({ title, collapsed }) {
  if (collapsed) return <Divider sx={{ my: 0.5 }} />
  return (
    <Typography
      variant="caption"
      sx={{
        px: 2,
        py: 0.75,
        display: 'block',
        color: 'text.disabled',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontSize: '0.65rem',
      }}
    >
      {title}
    </Typography>
  )
}

function NavItem({ icon, label, href, active, collapsed, onClick, 'data-testid': testId }) {
  const linkProps = onClick
    ? { onClick }
    : { component: Link, href }

  return (
    <Tooltip title={collapsed ? label : ''} placement="right">
      <ListItem disablePadding>
        <ListItemButton
          selected={active}
          {...linkProps}
          data-testid={testId}
          sx={{
            minHeight: 36,
            px: collapsed ? 0 : 2.5,
            justifyContent: collapsed ? 'center' : 'flex-start',
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 0 : 36,
              color: 'inherit',
            }}
          >
            {icon}
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={label}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400 }}
            />
          )}
        </ListItemButton>
      </ListItem>
    </Tooltip>
  )
}

export default function AppShell({ children }) {
  const { props } = usePage()
  const { auth = {}, tenant = {}, current_property, flash = {} } = props
  const [collapsed, setCollapsed] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    if (flash.notice) setSnackbar({ open: true, message: flash.notice, severity: 'success' })
    else if (flash.alert) setSnackbar({ open: true, message: flash.alert, severity: 'error' })
  }, [flash.notice, flash.alert])

  const url = usePage().url
  const tenantSlug = tenant.slug || ''
  const hasPropertyContext = !!current_property

  const isActive = (path) => url.startsWith(path) || url === path

  const mainDrawerWidth = collapsed ? MAIN_DRAWER_COLLAPSED : MAIN_DRAWER_WIDTH

  const propertyBasePath = hasPropertyContext
    ? `/m/${tenantSlug}/properties/${current_property.id}`
    : ''

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top AppBar - full width, always visible */}
      <AppBar
        position="static"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexShrink: 0,
        }}
      >
        <Toolbar
          disableGutters
          sx={{ gap: 1.5, minHeight: `${APPBAR_HEIGHT}px !important`, pr: 2 }}
        >
          {/* Burger icon - aligned with main nav icons (centered in collapsed-width column) */}
          <Box
            sx={{
              width: MAIN_DRAWER_COLLAPSED,
              display: 'flex',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconButton
              onClick={() => setCollapsed(!collapsed)}
              size="small"
              data-testid="sidebar-toggle"
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Tenant name - always visible */}
          <Typography
            component={Link}
            href={`/m/${tenantSlug}`}
            variant="subtitle2"
            fontWeight={700}
            sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none' }}
            data-testid="tenant-name"
          >
            {tenant.name || 'Okonomi HV'}
          </Typography>

          <Box sx={{ flex: 1 }} />

          {/* Current property indicator - name on first line, city on second */}
          {hasPropertyContext && (
            <>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                data-testid="appbar-property-info"
              >
                <LocationCityIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    sx={{ lineHeight: 1.1 }}
                  >
                    {current_property.name}
                  </Typography>
                  {current_property.city && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ lineHeight: 1.1 }}
                    >
                      {current_property.city}
                    </Typography>
                  )}
                </Box>
              </Box>
              {current_property.time_lapse_enabled && (
                <TimeLapseControl
                  tenantSlug={tenantSlug}
                  propertyId={current_property.id}
                  simulatedDate={current_property.simulated_date}
                />
              )}
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Body: sidebars + content, all below the AppBar */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Main Sidebar - always visible, collapses to icon-only */}
        <Box
          component="nav"
          sx={{
            width: mainDrawerWidth,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: 'background.paper',
            transition: 'width 0.2s ease',
          }}
        >
          <List dense sx={{ py: 1, flex: 1 }}>
            <NavItem
              icon={<DashboardIcon fontSize="small" />}
              label="Dashboard"
              href={`/m/${tenantSlug}`}
              active={url === `/m/${tenantSlug}`}
              collapsed={collapsed}
            />

            <Box sx={{ mt: 1 }}>
              <NavSection title="Portfolio" collapsed={collapsed} />
              <NavItem
                icon={<ApartmentIcon fontSize="small" />}
                label="Immobilien"
                href={`/m/${tenantSlug}/properties`}
                active={isActive(`/m/${tenantSlug}/properties`)}
                collapsed={collapsed}
                data-testid="nav-properties"
              />
              <NavItem
                icon={<ContactsIcon fontSize="small" />}
                label="Kontakte"
                href={`/m/${tenantSlug}/contacts`}
                active={isActive(`/m/${tenantSlug}/contacts`)}
                collapsed={collapsed}
              />
            </Box>

            {auth?.role === 'admin' && (
              <Box sx={{ mt: 1 }} data-testid="nav-settings-section">
                <NavSection title="Einstellungen" collapsed={collapsed} />
                <NavItem
                  icon={<PeopleIcon fontSize="small" />}
                  label="Benutzer"
                  href={`/m/${tenantSlug}/settings/users`}
                  active={isActive(`/m/${tenantSlug}/settings/users`)}
                  collapsed={collapsed}
                  data-testid="nav-settings-users"
                />
                <NavItem
                  icon={<MailIcon fontSize="small" />}
                  label="Einladungen"
                  href={`/m/${tenantSlug}/settings/invitations`}
                  active={isActive(`/m/${tenantSlug}/settings/invitations`)}
                  collapsed={collapsed}
                  data-testid="nav-settings-invitations"
                />
                <NavItem
                  icon={<ShieldIcon fontSize="small" />}
                  label="Sicherheit"
                  href={`/m/${tenantSlug}/settings/security`}
                  active={isActive(`/m/${tenantSlug}/settings/security`)}
                  collapsed={collapsed}
                  data-testid="nav-settings-security"
                />
              </Box>
            )}
          </List>

          {/* User avatar at bottom of main sidebar */}
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              p: 1,
            }}
          >
            <Tooltip title={collapsed ? (auth?.user?.email || '') : ''} placement="right">
              <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  cursor: 'pointer',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
                data-testid="user-menu-trigger"
              >
                <Avatar
                  src={auth?.user?.avatar_url || undefined}
                  sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main' }}
                >
                  {auth?.user?.email?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                {!collapsed && (
                  <Typography variant="body2" noWrap sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {auth?.user?.email || ''}
                  </Typography>
                )}
              </Box>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
            >
              {auth?.user?.email && (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {auth.user.email}
                  </Typography>
                </MenuItem>
              )}
              <Divider />
              <MenuItem
                component={Link}
                href={`/m/${tenantSlug}/profile`}
                onClick={() => setAnchorEl(null)}
              >
                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                Profil
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null)
                  const form = document.createElement('form')
                  form.method = 'POST'
                  form.action = '/logout'
                  const csrf = document.querySelector('meta[name="csrf-token"]')?.content
                  const t = document.createElement('input')
                  t.type = 'hidden'; t.name = 'authenticity_token'; t.value = csrf || ''
                  const m = document.createElement('input')
                  m.type = 'hidden'; m.name = '_method'; m.value = 'DELETE'
                  form.appendChild(t); form.appendChild(m)
                  document.body.appendChild(form)
                  form.submit()
                }}
              >
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                Abmelden
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Property Sidebar - only visible when a property is selected */}
        {hasPropertyContext && (
          <Box
            component="nav"
            data-testid="property-sidebar"
            sx={{
              width: PROPERTY_DRAWER_WIDTH,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              overflowX: 'hidden',
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              <List dense sx={{ py: 1 }}>
                <NavSection title="Verwaltung" />
                <NavItem
                  icon={<HomeWorkIcon fontSize="small" />}
                  label="Übersicht"
                  href={propertyBasePath}
                  active={url === propertyBasePath}
                  data-testid="nav-property-overview"
                />
                <NavItem
                  icon={<ViewListIcon fontSize="small" />}
                  label="Einheiten"
                  href={`${propertyBasePath}/units`}
                  active={isActive(`${propertyBasePath}/units`) && !isActive(`${propertyBasePath}/units/attributes`)}
                  data-testid="nav-units"
                />
                <NavItem
                  icon={<EditNoteIcon fontSize="small" />}
                  label="Attributwerte"
                  href={`${propertyBasePath}/units/attributes`}
                  active={isActive(`${propertyBasePath}/units/attributes`)}
                  data-testid="nav-attribute-values"
                />
                <NavItem
                  icon={<DescriptionIcon fontSize="small" />}
                  label="Verträge"
                  href={`${propertyBasePath}/contracts`}
                  active={isActive(`${propertyBasePath}/contracts`)}
                  data-testid="nav-contracts"
                />
                <NavItem
                  icon={<TuneIcon fontSize="small" />}
                  label="Attributtypen"
                  href={`${propertyBasePath}/unit_attribute_types`}
                  active={isActive(`${propertyBasePath}/unit_attribute_types`)}
                  data-testid="nav-attribute-types"
                />
                {current_property.management_type === 'weg' && (
                  <NavItem
                    icon={<GroupsIcon fontSize="small" />}
                    label="Versammlungen"
                    href={`${propertyBasePath}/assemblies`}
                    active={isActive(`${propertyBasePath}/assemblies`)}
                    data-testid="nav-assemblies"
                  />
                )}

                <NavSection title="Finanzen" />
                <NavItem
                  icon={<ReceiptIcon fontSize="small" />}
                  label="Sollstellungen"
                  href={`${propertyBasePath}/receivables`}
                  active={isActive(`${propertyBasePath}/receivables`)}
                />
                <NavItem
                  icon={<ReceiptIcon fontSize="small" />}
                  label="Verbindlichkeiten"
                  href={`${propertyBasePath}/payables`}
                  active={isActive(`${propertyBasePath}/payables`)}
                  data-testid="nav-payables"
                />
                <NavItem
                  icon={<BalanceIcon fontSize="small" />}
                  label="OP-Liste"
                  href={`${propertyBasePath}/open_items`}
                  active={isActive(`${propertyBasePath}/open_items`)}
                />
                <NavItem
                  icon={<DescriptionIcon fontSize="small" />}
                  label="Rechnungen"
                  href={`${propertyBasePath}/invoices`}
                  active={isActive(`${propertyBasePath}/invoices`)}
                />
                <NavItem
                  icon={<AccountBalanceIcon fontSize="small" />}
                  label="Bankkonten"
                  href={`${propertyBasePath}/bank_accounts`}
                  active={isActive(`${propertyBasePath}/bank_accounts`)}
                  data-testid="nav-bank-accounts"
                />

                <NavSection title="Buchhaltung" />
                <NavItem
                  icon={<AccountBalanceIcon fontSize="small" />}
                  label="Geschäftsjahre"
                  href={`${propertyBasePath}/fiscal_years`}
                  active={isActive(`${propertyBasePath}/fiscal_years`)}
                />
                <NavItem
                  icon={<AccountBalanceIcon fontSize="small" />}
                  label="Kontenplan"
                  href={`${propertyBasePath}/chart_of_accounts`}
                  active={isActive(`${propertyBasePath}/chart_of_accounts`)}
                />
                <NavItem
                  icon={<AccountBalanceIcon fontSize="small" />}
                  label="Saldenliste"
                  href={`${propertyBasePath}/balance_sheet`}
                  active={isActive(`${propertyBasePath}/balance_sheet`) || isActive(`${propertyBasePath}/ledger_accounts`)}
                />
                <NavItem
                  icon={<AccountBalanceIcon fontSize="small" />}
                  label="Journal"
                  href={`${propertyBasePath}/journal`}
                  active={isActive(`${propertyBasePath}/journal`)}
                />
                <NavItem
                  icon={<BalanceIcon fontSize="small" />}
                  label={current_property.management_type === 'weg' ? 'Wirtschaftsplan' : 'Budget'}
                  href={`${propertyBasePath}/budget_plans`}
                  active={isActive(`${propertyBasePath}/budget_plans`)}
                />
                <NavItem
                  icon={<BalanceIcon fontSize="small" />}
                  label="Abrechnung"
                  href={`${propertyBasePath}/settlements`}
                  active={isActive(`${propertyBasePath}/settlements`)}
                />
                <NavItem
                  icon={<BalanceIcon fontSize="small" />}
                  label="Verteilerschlüssel"
                  href={`${propertyBasePath}/distribution_keys`}
                  active={isActive(`${propertyBasePath}/distribution_keys`)}
                />
                <NavItem
                  icon={<TuneIcon fontSize="small" />}
                  label="Kostenkategorien"
                  href={`${propertyBasePath}/cost_categories`}
                  active={isActive(`${propertyBasePath}/cost_categories`)}
                />

                {/* WEG-specific section */}
                {current_property.management_type === 'weg' && (
                  <>
                    <NavSection title="WEG" />
                    <NavItem
                      icon={<GavelIcon fontSize="small" />}
                      label="Beschlusssammlung"
                      href={`${propertyBasePath}/resolutions`}
                      active={isActive(`${propertyBasePath}/resolutions`)}
                      data-testid="nav-resolutions"
                    />
                    <NavItem
                      icon={<AttachMoneyIcon fontSize="small" />}
                      label="Sonderumlagen"
                      href={`${propertyBasePath}/special_levies`}
                      active={isActive(`${propertyBasePath}/special_levies`)}
                      data-testid="nav-special-levies"
                    />
                  </>
                )}
              </List>
            </Box>
          </Box>
        )}

        {/* Page content */}
        <Box
          component="main"
          sx={{ flex: 1, overflowY: 'auto', p: 3, backgroundColor: 'background.default' }}
        >
          {children}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          data-testid="flash-message"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
